import { GeoImageKind } from '../geo-image-upload-modal/geo-image-upload.models';

/** A tag as the gallery API returns it. */
export interface UploadedGeoImageTag {
  id: string;
  name: string;
}

/** One gallery row (contract `GetGallery`). */
export interface UploadedGeoImageListItem {
  id: string;
  title: string;
  imageKind: GeoImageKind;
  regionId: string | null;
  regionName: string | null;
  parentRegionId: string | null;
  parentRegionName: string | null;
  /** The original file. */
  fileStorageId: string;
  /** Generated JPEG thumbnail, set for rasters only. */
  previewFileStorageId: string | null;
  fileContentType: string;
  /** Set for a `Photo`. */
  latitude: number | null;
  longitude: number | null;
  /** WKT polygon in EPSG:4326, set for a `Raster`. */
  footprintWkt: string | null;
  uploadDateUtc: string;
  captureDateUtc: string | null;
  description: string | null;
  tags: UploadedGeoImageTag[];
}

export interface UploadedGeoImageGalleryResponse {
  items: UploadedGeoImageListItem[];
  total: number;
}

/** Request body of `GetGallery`; every field is optional. */
export interface UploadedGeoImageGalleryRequest {
  ids?: string[];
  regionId?: string | null;
  parentRegionId?: string | null;
  tagIds?: string[];
  captureDateFrom?: string | null;
  captureDateTo?: string | null;
  uploadDateFrom?: string | null;
  uploadDateTo?: string | null;
  search?: string | null;
  north?: number | null;
  south?: number | null;
  east?: number | null;
  west?: number | null;
  offset?: number;
  limit?: number;
  sortBy?: string | null;
  sortDescending?: boolean;
}

export interface UploadedGeoImageRegionNode {
  id: string;
  name: string;
  parentId: string | null;
  children: UploadedGeoImageRegionNode[];
}

/** Filter options (contract `GetMetadata`). */
export interface UploadedGeoImageMetadata {
  /** Regions that contain at least one image, plus their ancestors, as a tree. */
  regions: UploadedGeoImageRegionNode[];
  tags: UploadedGeoImageTag[];
  minCaptureDateUtc: string | null;
  maxCaptureDateUtc: string | null;
  minUploadDateUtc: string | null;
  maxUploadDateUtc: string | null;
}

/** Body of `PUT UpdateUploadedGeoImage`; an omitted field keeps its current value. */
export interface UploadedGeoImageUpdateRequest {
  id: string;
  title?: string;
  description?: string;
  /** Full replacement set; omit to keep the current tags, send `[]` to remove them all. */
  tagIds?: string[];
}

/** Filter state edited in the filter panel. */
export interface GeoImageFilters {
  regionId: string | null;
  tagIds: string[];
  captureDateFrom: Date | null;
  captureDateTo: Date | null;
  search: string;
}

export function emptyGeoImageFilters(): GeoImageFilters {
  return { regionId: null, tagIds: [], captureDateFrom: null, captureDateTo: null, search: '' };
}

export function hasActiveGeoImageFilters(filters: GeoImageFilters): boolean {
  return (
    !!filters.regionId ||
    filters.tagIds.length > 0 ||
    !!filters.captureDateFrom ||
    !!filters.captureDateTo ||
    filters.search.trim().length > 0
  );
}

/** Map viewport, as Leaflet reports it. */
export interface GeoImageMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}
